import React, { useState } from 'react'
import { DonorData } from '../types'
import { formatCurrency } from '../utils/formatCurrency'
import { useDonation } from '../context/DonationContext'
import { sendThankYouEmail } from '../actions/sendThankYou'
import TestEmailModal from './TestEmailModal'
import Image from 'next/image'

interface ThankYouModalProps {
  donor: DonorData | null
  isOpen: boolean
  onClose: () => void
  sendingDonors: Set<string>
  setSendingDonors: React.Dispatch<React.SetStateAction<Set<string>>>
}

export default function ThankYouModal({ 
  donor, 
  isOpen, 
  onClose, 
  setSendingDonors 
}: ThankYouModalProps) {
  const { markThankYouSent } = useDonation()
  const { filterStartDate, filterEndDate } = useDonation().state
  const [showTestEmailModal, setShowTestEmailModal] = useState(false)
  const testEmail = typeof window !== 'undefined' ? localStorage.getItem('testEmail') : null

  if (!isOpen || !donor) return null

  // Format the current date
  const formattedDate = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })

  // Format the filter dates
  const formattedStartDate = filterStartDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })

  const formattedEndDate = filterEndDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })

  // Create a map of donations by month
  const donationsByMonth = donor?.donations.reduce((acc, donation) => {
    const month = new Date(donation['Created at']).getMonth()
    acc[month] = (acc[month] || 0) + parseFloat(donation.Amount)
    return acc
  }, {} as Record<number, number>) || {}

  // Calculate total
  const totalAmount = Object.values(donationsByMonth).reduce((sum, amount) => sum + amount, 0)

  // Create array of all months for header
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ]

  const handleSendEmail = async () => {
    if (!donor) return
    
    const dateRange = {
      start: filterStartDate.toISOString(),
      end: filterEndDate.toISOString()
    }
    
    setSendingDonors(prev => {
      const newSet = new Set(prev)
      newSet.add(donor.id)
      return newSet
    })
    
    try {
      const result = await sendThankYouEmail({
        name: donor.name,
        email: donor.email,
        total: donor.total,
        dateRange,
        donations: donor.donations
      })
      
      if (result.success) {
        markThankYouSent(donor.email, donor.name, dateRange)
        onClose()
      } else {
        console.error('Failed to send email:', result.error)
      }
    } catch (error) {
      console.error('Error in handleSendEmail:', error)
    } finally {
      setSendingDonors(prev => {
        const newSet = new Set(prev)
        newSet.delete(donor.id)
        return newSet
      })
    }
  }

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      setShowTestEmailModal(true)
      return
    }
    
    if (!donor) return
    
    const dateRange = {
      start: filterStartDate.toISOString(),
      end: filterEndDate.toISOString()
    }
    
    setSendingDonors(prev => {
      const newSet = new Set(prev)
      newSet.add(donor.id)
      return newSet
    })
    
    try {
      const result = await sendThankYouEmail({
        name: donor.name,
        email: testEmail,
        total: donor.total,
        dateRange,
        donations: donor.donations
      }, true)
      
      if (result.success) {
        onClose()
      } else {
        console.error('Failed to send test email:', result.error)
      }
    } catch (error) {
      console.error('Error in handleSendTestEmail:', error)
    } finally {
      setSendingDonors(prev => {
        const newSet = new Set(prev)
        newSet.delete(donor.id)
        return newSet
      })
    }
  }

  const handleSaveTestEmail = (email: string) => {
    localStorage.setItem('testEmail', email)
  }

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center p-4">
      <div className="bg-white p-6 rounded-lg w-[80vw] max-w-6xl flex flex-col max-h-[90vh]">
        <div className="flex justify-end mb-4">
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">Close</button>
        </div>
        
        <div className="overflow-y-auto flex-1">
          <div className="header text-center mb-4">
            <div className="logo mb-4">
              <Image 
                src="/assets/logo.png" 
                alt="IGEM Logo"
                width={100}
                height={100}
                className="max-w-[100px] mx-auto"
              />
            </div>
            <h2 className="text-xl font-bold mb-2 text-secondary">INTERNATIONAL GOLDEN EAGLE MINISTRIES</h2>
            <div>DBA IGEM</div>
            <div>PO Box 18194</div>
            <div>Anaheim, CA 92817</div>
            <div><a href="https://www.intlgem.org" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">www.intlgem.org</a></div>
          </div>

          <div className="date mb-4">
            <div>{formattedDate}</div>
          </div>

          <div className="address mb-4">
            <div>{donor?.name}</div>
            {(() => {
              // Get the most recent donation
              const lastDonation = donor?.donations
                .sort((a, b) => new Date(b['Created at']).getTime() - new Date(a['Created at']).getTime())[0]
              
              return (
                <>
                  <div>{lastDonation?.Address1}</div>
                  {lastDonation?.Address2 && <div>{lastDonation?.Address2}</div>}
                  <div>
                    {lastDonation?.City}
                    {lastDonation?.['State/Province'] && `, ${lastDonation?.['State/Province']}`} 
                    {lastDonation?.Zip && ` ${lastDonation?.Zip}`}
                  </div>
                  {lastDonation?.Country && <div>{lastDonation?.Country}</div>}
                </>
              )
            })()}
          </div>

          <p className="mb-8">
            Thank you for your donations to our organization for the year of {filterStartDate.getFullYear()}.
            Your contributions enable us to reach the lost and hurting in our community and around the world with the Gospel of Jesus Christ.
          </p>

          <h3 className="mt-8 font-semibold">Contribution Report</h3>
          <p>{formattedStartDate} to {formattedEndDate}</p>

          <table className="w-full border-collapse my-8">
            <thead className="sm:table-header-group hidden">
              <tr>
                {months.map(month => (
                  <th key={month} className="p-2 text-xs font-medium text-gray-500 uppercase tracking-wider text-center border border-gray-200 bg-gray-50">
                    {month}
                  </th>
                ))}
                <th className="p-2 text-xs font-medium text-gray-500 uppercase tracking-wider text-center border border-gray-200 bg-gray-50">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="sm:table-row-group block">
              <tr className="sm:table-row block sm:border-none border border-gray-200 mb-4">
                {months.map((month, index) => (
                  <td 
                    key={index} 
                    data-label={month}
                    className="sm:table-cell sm:p-2 block sm:text-center sm:border-gray-200 sm:border relative p-2 pl-[50%] text-right border-b border-gray-200 before:sm:hidden before:content-[attr(data-label)] before:absolute before:left-2 before:top-2 before:w-[45%] before:pr-2 before:text-left before:font-bold before:text-gray-600"
                  >
                    {donationsByMonth[index] ? formatCurrency(donationsByMonth[index]) : '-'}
                  </td>
                ))}
                <td className="sm:table-cell sm:p-2 block sm:text-center sm:font-bold sm:bg-gray-50 sm:border-gray-200 sm:border relative p-2 pl-[50%] text-right border-b border-gray-200 before:content-[attr(data-label)] before:absolute before:left-2 before:top-2 before:w-[45%] before:pr-2 before:text-left before:font-bold before:text-gray-600 bg-gray-50 font-bold">
                  {formatCurrency(totalAmount)}
                </td>
              </tr>
            </tbody>
          </table>

          <p>Please retain this document as proof of your charitable contributions. We look forward to your continued support.</p>

          <p className="mt-5">Sincerely,</p>
          <p>International Golden Eagle Ministries</p>

          <div className="footer-note mt-24 text-xs text-gray-500">
            <p>Remark: IGEM is a 501(c)(3) non-profit organization. Federal ID#46-3215674. Per IRS regulations, we state that no goods or services were received in exchange for this donation.</p>
          </div>
        </div>
        
        <div className="mt-8 flex justify-end gap-4 pt-4 border-t">
          {testEmail && (
            <button
              onClick={() => setShowTestEmailModal(true)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Modify Test Email
            </button>
          )}
          <button
            onClick={handleSendTestEmail}
            className="px-4 py-2 bg-secondary text-white rounded hover:bg-secondary/80"
          >
            Send Test Email
          </button>
          <button 
            onClick={onClose} 
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button 
            onClick={handleSendEmail}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/80"
          >
            Send Email
          </button>
        </div>
      </div>
      <TestEmailModal
        isOpen={showTestEmailModal}
        onClose={() => setShowTestEmailModal(false)}
        onSave={handleSaveTestEmail}
        currentEmail={testEmail || undefined}
      />
    </div>
  )
}

